export type QuoteOverlapDetail = {
  event_id: string;
  event_code: string;
  event_description: string | null;
  order_code: string | null;
  customer_name: string | null;
  start_at: string | null;
  end_at: string | null;
  qty: number;
  /** Peak requested quantity for the event in the selected window(s). */
  requested_qty?: number | null;
  /** Effective hard hold used by availability; null while the event is tentative. */
  capacity_hold_qty?: number | null;
  status: string;
  order_type: string;
};

export type PlanningReturnDependency = {
  key: string;
  qty: number;
  reason: 'expected_return' | 'overdue_return' | 'unknown_return' | 'ambiguous_return' | 'unknown_return_location';
  eventCode: string | null;
  expectedReturnAt: string | null;
};

export const normalisePlanningReturns = (input: unknown): PlanningReturnDependency[] => Array.isArray(input)
  ? input.flatMap((entry: unknown) => {
    if (!entry || typeof entry !== 'object') return [];
    const value = entry as Record<string, unknown>;
    if (typeof value.key !== 'string' || !['expected_return', 'overdue_return', 'unknown_return', 'ambiguous_return', 'unknown_return_location'].includes(String(value.reason))) return [];
    return [{ key: value.key, qty: Math.max(toNumber(value.qty), 0), reason: value.reason as PlanningReturnDependency['reason'], eventCode: value.event_code == null ? null : String(value.event_code), expectedReturnAt: value.expected_return_at == null ? null : String(value.expected_return_at) }];
  }) : [];

/** One physical source stays one dependency across dates and kit components. */
export const mergePlanningReturns = (snapshots: Array<Pick<AvailabilitySnapshot, 'planningReturns'>>): PlanningReturnDependency[] => {
  const entries = new Map<string, PlanningReturnDependency>();
  const priority = { expected_return: 0, overdue_return: 1, unknown_return: 2, unknown_return_location: 2, ambiguous_return: 3 };
  for (const snapshot of snapshots) for (const entry of snapshot.planningReturns ?? []) {
    const previous = entries.get(entry.key);
    if (!previous) entries.set(entry.key, entry);
    else entries.set(entry.key, {
      ...(priority[entry.reason] > priority[previous.reason] ? entry : previous),
      qty: Math.max(entry.qty, previous.qty),
    });
  }
  return [...entries.values()];
};

type PlanningReturnContext = Pick<AvailabilitySnapshot, 'planningReturns'>
  & Partial<Pick<AvailabilitySnapshot, 'currentLineQty' | 'freeBeforeCurrentQty' | 'returnDependencyRisk'>>;

/** A conservative sufficiency proof, never a replacement stock/hold figure. */
export const hasPlanningReturnRisk = (snapshot: PlanningReturnContext): boolean => {
  const entries = mergePlanningReturns([snapshot]).filter(entry => entry.reason !== 'expected_return' && entry.qty > 0);
  if (!entries.length) return false;
  // Aggregates inherit each dated component's assessment rather than comparing
  // one child's free capacity with another child's need/uncertain units.
  if (snapshot.returnDependencyRisk != null) return snapshot.returnDependencyRisk;
  const need = snapshot.currentLineQty;
  const free = snapshot.freeBeforeCurrentQty;
  const uncertain = entries.reduce((sum, entry) => sum + entry.qty, 0);
  if (need === 0) return false;
  if (need == null || free == null || !Number.isFinite(need) || !Number.isFinite(free) || !Number.isFinite(uncertain)) return true;
  // Canonical free-before is the boundary minimum, including a firm event's
  // priority. Protected availableQty can be capped at need and is not this fact.
  // The union bounds uncertain capacity at every boundary, including transfers.
  return Math.max(free - uncertain, 0) < need;
};

const planningReturnSummary = (snapshot: Pick<AvailabilitySnapshot, 'planningReturns'>): { quantities: string; orders: string } | null => {
  const entries = mergePlanningReturns([snapshot]).filter(entry => entry.reason !== 'expected_return' && entry.qty > 0);
  if (!entries.length) return null;
  const overdue = entries.filter(entry => entry.reason === 'overdue_return').reduce((sum, entry) => sum + entry.qty, 0);
  const unknown = entries.filter(entry => entry.reason === 'unknown_return').reduce((sum, entry) => sum + entry.qty, 0);
  const ambiguous = entries.filter(entry => entry.reason === 'ambiguous_return').reduce((sum, entry) => sum + entry.qty, 0);
  const site = entries.filter(entry => entry.reason === 'unknown_return_location').reduce((sum, entry) => sum + entry.qty, 0);
  const orders = [...new Set(entries.flatMap(entry => entry.eventCode ? [entry.eventCode] : []))];
  return {
    quantities: [overdue ? `${overdue.toLocaleString()} overdue` : '', unknown ? `${unknown.toLocaleString()} with no confirmed return time` : '', ambiguous ? `${ambiguous.toLocaleString()} with conflicting return evidence` : '', site ? `${site.toLocaleString()} with an unconfirmed return site` : ''].filter(Boolean).join(' and '),
    orders: orders.length ? ` Orders: ${orders.slice(0, 5).join(', ')}${orders.length > 5 ? ` and ${orders.length - 5} more` : ''}.` : '',
  };
};

export const planningReturnWarning = (snapshot: PlanningReturnContext): string | null => {
  const summary = planningReturnSummary(snapshot);
  return summary && hasPlanningReturnRisk(snapshot)
    ? `Forecast may depend on ${summary.quantities}. Check returns before fulfillment.${summary.orders}`
    : null;
};

/** Retain custody evidence even when those units cannot change this request's fit. */
export const planningReturnNote = (snapshot: PlanningReturnContext): string | null => {
  const summary = planningReturnSummary(snapshot);
  return summary && !hasPlanningReturnRisk(snapshot)
    ? `Return records include ${summary.quantities}. This request is covered without those units.${summary.orders}`
    : null;
};

export type AvailabilityQuantityWitness = {
  boundaryAt: string;
  usageDate: string | null;
  targetCurrentQty: number;
  protectedCurrentQty: number;
  freeBeforeCurrentQty: number;
  spareAfterCurrentQty: number;
};

export type AvailabilitySnapshot = {
  totalStock: number;
  reservedQty: number;
  availableQty: number;
  /** Canonical server facts: protection is distinct from unused shared capacity. */
  freeBeforeCurrentQty?: number;
  spareAfterCurrentQty?: number;
  quantityBasis?: string;
  quantityWitnesses?: Partial<Record<'protected' | 'free_before' | 'spare_after', AvailabilityQuantityWitness>>;
  overlapQuoteQty: number;
  quoteCount: number;
  worstCaseShortage: number;
  currentLineQty: number;
  /** Confirmed incoming transfer capacity. Already factored into availableQty. */
  transferInQty: number;
  /** Confirmed outgoing transfer commitment. Already factored into availableQty. */
  transferOutQty: number;
  /** Peak eligible supply in the selected window, already included in availability. */
  vendorIncomingQty?: number;
  vendorReceivedQty?: number;
  details: QuoteOverlapDetail[];
  /** Whether the row is stock-constrained, unlimited, or nonphysical. */
  availabilityMode?: 'tracked' | 'unlimited' | 'not_required';
  /** Firm tranche snapshots describe this event's protected capacity. */
  capacityBasis?: 'event_allocation' | 'shared_stock';
  status: 'green' | 'yellow' | 'orange' | 'red' | 'unknown';
  tooltip: string;
  /** Dated forecast dependencies; these never constitute a recorded return. */
  planningReturns?: PlanningReturnDependency[];
  /** Dated child assessment; false proves enough capacity without uncertain returns. */
  returnDependencyRisk?: boolean;
  /**
   * True when the availability read did not return this candidate (or, for an All-days rollup, at
   * least one of its dates). Every quantity is then a placeholder: never show it as stock or as a
   * shortage. See isUnresolvedSnapshot.
   */
  unresolved?: boolean;
  /** All-days rollups: how many dated rows resolved, and how many did not. */
  checkedCount?: number;
  unresolvedCount?: number;
  /** Saved-member coverage failed; this is not an extra failed date/RPC result. */
  coverageIncomplete?: boolean;
  /**
   * All-days rollups: the one date with the least room (its need minus its free stock is the
   * largest). The rollup's own figures are peaks that can come from different dates, so a verdict
   * compares need and free stock on this date only.
   */
  tightest?: {
    currentLineQty: number; availableQty: number;
    totalStock?: number; reservedQty?: number; overlapQuoteQty?: number;
    transferInQty?: number; transferOutQty?: number;
    freeBeforeCurrentQty?: number; spareAfterCurrentQty?: number;
    quantityBasis?: string; quantityWitnesses?: AvailabilitySnapshot['quantityWitnesses'];
  };
};

/** The read behind this snapshot did not come back, so its figures are not real. */
export const isUnresolvedSnapshot = (snapshot: AvailabilitySnapshot | null | undefined): boolean =>
  Boolean(snapshot?.unresolved);

export type AvailabilityMetrics = {
  totalStock: number;
  reservedQty: number;
  availableQty: number;
  overlapQuoteQty: number;
  quoteCount: number;
  worstCaseShortage: number;
  currentLineQty: number;
  transferInQty: number;
  transferOutQty: number;
  vendorIncomingQty?: number;
  vendorReceivedQty?: number;
};

export type InventoryOverlapWindow = QuoteOverlapDetail & {
  shortage?: number;
};

export type InventoryAvailability = {
  snapshot: AvailabilitySnapshot;
  overlaps: InventoryOverlapWindow[];
};

export type ProductAvailability = {
  status: string;
  bestItemId: string | null;
  fulfillQty: number;
  directQty: number;
  transferQty: number;
  usedMix: boolean;
  sameItemRequired: boolean | null;
};

const toNumber = (value: unknown): number => {
  const num = Number(value);
  return Number.isFinite(num) ? Number(num.toFixed(4)) : 0;
};

const detailDateFormatter = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: '2-digit',
});

export const formatDateRange = (startIso: string | null, endIso: string | null): string => {
  const startValue = startIso ? detailDateFormatter.format(new Date(startIso)) : 'N/A';
  const endValue = endIso ? detailDateFormatter.format(new Date(endIso)) : 'N/A';
  return `${startValue} -> ${endValue}`;
};

export const normaliseQuoteDetails = (input: unknown): QuoteOverlapDetail[] => {
  if (!Array.isArray(input)) return [];
  return (input as any[])
    .map(entry => {
      const eventId = entry?.event_id ? String(entry.event_id) : '';
      if (!eventId) return null;
      return {
        event_id: eventId,
        event_code: entry?.event_code ? String(entry.event_code) : eventId,
        event_description: entry?.event_description == null ? null : String(entry.event_description),
        order_code: entry?.order_code == null ? null : String(entry.order_code),
        customer_name: entry?.customer_name == null ? null : String(entry.customer_name),
        start_at: entry?.start_at ? String(entry.start_at) : null,
        end_at: entry?.end_at ? String(entry.end_at) : null,
        qty: toNumber(entry?.qty),
        requested_qty: entry?.requested_qty == null
          ? null
          : toNumber(entry.requested_qty),
        capacity_hold_qty: entry?.capacity_hold_qty == null
          ? null
          : toNumber(entry.capacity_hold_qty),
        status: entry?.status ? String(entry.status) : '',
        order_type: entry?.order_type ? String(entry.order_type) : '',
      } as QuoteOverlapDetail;
    })
    .filter((row): row is QuoteOverlapDetail => row !== null);
};

export const computeIndicatorStatus = (
  metrics: AvailabilityMetrics,
  options?: {
    isConfirmed?: boolean;
    useServerConfirmedShortage?: boolean;
  },
): AvailabilitySnapshot['status'] => {
  if (metrics.availableQty >= 999999000 || metrics.totalStock >= 999999000) {
    return 'green';
  }
  if (options?.isConfirmed && options.useServerConfirmedShortage) {
    return metrics.worstCaseShortage > 0.0001 ? 'red' : 'green';
  }
  // Confirmed inbound inventory can satisfy an event even when nothing is
  // physically present at the location yet. availableQty already includes the
  // inbound quantity, but retain the explicit transfer check so inconsistent
  // zero-stock records cannot become green merely from a stray available value.
  const hasUsableInboundCapacity =
    metrics.transferInQty > 0.0001 &&
    metrics.availableQty > 0.0001;

  // Red when the location has neither local stock nor usable inbound capacity,
  // or when all effective capacity has already been consumed.
  if (!(metrics.totalStock > 0) && !hasUsableInboundCapacity) return 'red';
  if (metrics.availableQty <= 0.0001) return 'red';

  const currentExceedsAvailable = metrics.currentLineQty > metrics.availableQty + 0.0001;

  // Confirmed events have hard reservations — overlapping quotes don't threaten
  // their allocation. Only check whether the confirmed event's own qty fits
  // within available stock (stock minus other confirmed reservations).
  if (options?.isConfirmed) {
    if (currentExceedsAvailable) return 'red';
    return 'green';
  }

  const reservedRatio = metrics.totalStock > 0 ? metrics.reservedQty / metrics.totalStock : 0;
  const hasQuotes = metrics.overlapQuoteQty > 0.0001;
  const shortage = metrics.worstCaseShortage > 0.0001;

  if (!shortage && !currentExceedsAvailable && reservedRatio <= 0.5) {
    return 'green';
  }

  if (shortage && (reservedRatio >= 1 || metrics.availableQty <= 0.0001)) {
    return 'red';
  }

  if (reservedRatio >= 0.75 && currentExceedsAvailable) {
    return 'orange';
  }

  if (shortage) {
    return 'orange';
  }

  if (reservedRatio > 0.5 && reservedRatio < 0.75 && hasQuotes) {
    return 'yellow';
  }

  if (currentExceedsAvailable) {
    return 'orange';
  }

  if (reservedRatio >= 0.75) {
    return 'yellow';
  }

  return shortage ? 'orange' : 'green';
};

export const buildSnapshotTooltip = (
  metrics: AvailabilityMetrics,
  details: QuoteOverlapDetail[],
  options?: {
    isConfirmed?: boolean;
    useServerConfirmedShortage?: boolean;
  },
): string => {
  const lines: string[] = [];
  
  // Stock section
  const reservedRatio = metrics.totalStock > 0 
    ? Math.round((metrics.reservedQty / metrics.totalStock) * 100) 
    : 0;
  lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  lines.push('STOCK');
  lines.push(`Total: ${metrics.totalStock.toLocaleString()}`);
  lines.push(`Reserved: ${metrics.reservedQty.toLocaleString()} (${reservedRatio}%)`);
  lines.push(`Available: ${metrics.availableQty.toLocaleString()}`);
  if((metrics.vendorReceivedQty??0)>0)lines.push(`Vendor stock received: up to ${metrics.vendorReceivedQty!.toLocaleString()} in this window (already included).`);
  if((metrics.vendorIncomingQty??0)>0)lines.push(`Committed incoming — awaiting receipt: up to ${metrics.vendorIncomingQty!.toLocaleString()} in this window (already included; cannot be picked).`);
  
  // Current line section
  lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  lines.push('CURRENT LINE');
  lines.push(`Requested: ${metrics.currentLineQty.toLocaleString()}`);
  const lineShortfall = options?.isConfirmed
    && options.useServerConfirmedShortage
    ? metrics.worstCaseShortage
    : Math.max(metrics.currentLineQty - metrics.availableQty, 0);
  if (lineShortfall > 0) {
    const shortfall = lineShortfall;
    lines.push(`⚠ Shortfall: ${shortfall.toLocaleString()}`);
  }
  
  // Overlap section
  if (metrics.overlapQuoteQty > 0 || details.length > 0) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('OVERLAPPING QUOTES');
    lines.push(`Total overlap qty: ${metrics.overlapQuoteQty.toLocaleString()} from ${metrics.quoteCount} event${metrics.quoteCount !== 1 ? 's' : ''}`);
    
    if (details.length > 0) {
      details.slice(0, 5).forEach(detail => {
        const statusLabel = detail.status ? detail.status.toLowerCase() : 'unknown';
        const typeLabel = detail.order_type ? detail.order_type.toLowerCase() : 'n/a';
        lines.push(
          `  • ${detail.event_code}${detail.customer_name ? ` (${detail.customer_name})` : ''} | ${statusLabel}/${typeLabel} | Qty ${detail.qty} | ${formatDateRange(detail.start_at, detail.end_at)}`,
        );
      });
      if (details.length > 5) {
        lines.push(`  ... and ${details.length - 5} more`);
      }
    }
  } else {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    lines.push('OVERLAPPING QUOTES');
    lines.push('None');
  }
  
  // Shortage section
  if (metrics.worstCaseShortage > 0) {
    lines.push('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    if (options?.isConfirmed) {
      lines.push('SHORTAGE');
      lines.push(`Short: ${metrics.worstCaseShortage.toLocaleString()}`);
    } else {
      lines.push('WORST-CASE SHORTAGE');
      lines.push(`Potential: ${metrics.worstCaseShortage.toLocaleString()}`);
    }
  }

  return lines.join('\n');
};

export type AvailabilityRecordInput = {
  total_stock?: number | string | null;
  reserved_qty?: number | string | null;
  available_qty?: number | string | null;
  overlap_quote_qty?: number | string | null;
  quote_count?: number | string | null;
  worst_case_shortage?: number | string | null;
  shortage_qty?: number | string | null;
  current_line_qty?: number | string | null;
  details?: unknown;
  /** Confirmed incoming transfer capacity — already factored into available_qty. */
  transfer_in_qty?: number | string | null;
  /** Confirmed outgoing transfer commitment — already factored into available_qty. */
  transfer_out_qty?: number | string | null;
};

export const buildAvailabilitySnapshotFromRecord = (
  record: AvailabilityRecordInput,
  options?: {
    fallbackCurrentLineQty?: number;
    detailsOverride?: QuoteOverlapDetail[];
    statusOverride?: AvailabilitySnapshot['status'];
    isConfirmed?: boolean;
  },
): AvailabilitySnapshot => {
  const rawDetailEntries = Array.isArray(record.details) ? record.details : [];
  const hasExactFirmTrancheAvailability = rawDetailEntries.some(
    entry => entry != null
      && typeof entry === 'object'
      && (entry as { inventory_quantity_tranches?: unknown }).inventory_quantity_tranches === true,
  );
  const availabilityMetadata = rawDetailEntries.find(
    entry => entry != null
      && typeof entry === 'object'
      && (entry as { availability_meta?: unknown }).availability_meta === true,
  ) as { transfer_in_qty?: unknown; transfer_out_qty?: unknown; vendor_incoming_qty?: unknown; vendor_received_qty?: unknown; planning_return_dependencies?: unknown } | undefined;
  const planningReturns = normalisePlanningReturns(availabilityMetadata?.planning_return_dependencies);
  const quantityMetadata = rawDetailEntries.find(entry => entry != null && typeof entry === 'object'
    && (entry as { inventory_quantity_facts?: unknown }).inventory_quantity_facts === true
    && (entry as { quantity_facts_version?: unknown }).quantity_facts_version === 1
    && (entry as { quantity_basis?: unknown }).quantity_basis === 'candidate_window_boundary_minimum') as Record<string, unknown> | undefined;
  const optionalQuantity = (value: unknown): number | undefined => {
    if (typeof value !== 'number' && (typeof value !== 'string' || value.trim() === '')) return undefined;
    return Number.isFinite(Number(value)) && Number(value) >= 0 ? toNumber(value) : undefined;
  };
  const quantityWitnesses: NonNullable<AvailabilitySnapshot['quantityWitnesses']> = {};
  const rawWitnesses = quantityMetadata?.quantity_witnesses;
  if (rawWitnesses && typeof rawWitnesses === 'object') {
    for (const key of ['protected', 'free_before', 'spare_after'] as const) {
      const raw = (rawWitnesses as Record<string, unknown>)[key];
      if (!raw || typeof raw !== 'object') continue;
      const witness = raw as Record<string, unknown>;
      const targetCurrentQty = optionalQuantity(witness.target_current_qty);
      const protectedCurrentQty = optionalQuantity(witness.protected_current_qty);
      const freeBeforeCurrentQty = optionalQuantity(witness.free_before_current_qty);
      const spareAfterCurrentQty = optionalQuantity(witness.spare_after_current_qty);
      if (typeof witness.boundary_at !== 'string' || targetCurrentQty == null || protectedCurrentQty == null || freeBeforeCurrentQty == null || spareAfterCurrentQty == null) continue;
      quantityWitnesses[key] = { boundaryAt: witness.boundary_at, usageDate: typeof witness.usage_date === 'string' ? witness.usage_date : null,
        targetCurrentQty, protectedCurrentQty, freeBeforeCurrentQty, spareAfterCurrentQty };
    }
  }
  const doesNotRequireStock = rawDetailEntries.some(
    entry => entry != null
      && typeof entry === 'object'
      && (entry as { no_stock?: unknown }).no_stock === true,
  );
  const rawTotalStock = toNumber(record.total_stock);
  const rawAvailableQty = toNumber(record.available_qty);
  // Nonphysical catalog rows (labor, freight, misc charges) are deliberately
  // non-constraining. Normalize them to the same arithmetic sentinel used for
  // unlimited inventory so kit and rollup calculations cannot turn them into
  // false shortages, while availabilityMode preserves the user-facing reason.
  const totalStock = doesNotRequireStock ? 999999999 : rawTotalStock;
  const reservedQty = Math.max(toNumber(record.reserved_qty), 0);
  const availableQty = doesNotRequireStock ? 999999999 : rawAvailableQty;
  const overlapQuoteQty = Math.max(toNumber(record.overlap_quote_qty), 0);
  const quoteCount = Number(record.quote_count ?? 0);
  const serverWorstCaseShortage = Math.max(toNumber(record.worst_case_shortage), 0);
  const serverShortageQty = Math.max(toNumber(record.shortage_qty), 0);
  const fallbackCurrentQty = options?.fallbackCurrentLineQty ?? 0;
  const currentLineQty = Math.max(toNumber(record.current_line_qty ?? fallbackCurrentQty), 0);
  const shortageFromAvailable = Math.max(0, -availableQty);
  const worstCaseShortage = doesNotRequireStock
    ? 0
    : options?.isConfirmed
      ? hasExactFirmTrancheAvailability
        ? Math.max(serverShortageQty, serverWorstCaseShortage, shortageFromAvailable)
        : Math.max(currentLineQty - availableQty, 0)
      : Math.max(serverShortageQty || 0, serverWorstCaseShortage, shortageFromAvailable);
  const transferInQty = Math.max(
    toNumber(record.transfer_in_qty ?? availabilityMetadata?.transfer_in_qty),
    0,
  );
  const transferOutQty = Math.max(
    toNumber(record.transfer_out_qty ?? availabilityMetadata?.transfer_out_qty),
    0,
  );

  const metrics: AvailabilityMetrics = {
    totalStock,
    reservedQty,
    availableQty,
    overlapQuoteQty,
    quoteCount,
    worstCaseShortage,
    currentLineQty,
    transferInQty,
    transferOutQty,
    vendorIncomingQty: Math.max(toNumber(availabilityMetadata?.vendor_incoming_qty),0),
    vendorReceivedQty: Math.max(toNumber(availabilityMetadata?.vendor_received_qty),0),
  };

  const details = options?.detailsOverride ?? normaliseQuoteDetails(record.details);
  const freeBeforeCurrentQty = optionalQuantity(quantityMetadata?.free_before_current_qty);
  const returnContext = { planningReturns, currentLineQty, freeBeforeCurrentQty };
  const returnDependencyRisk = !doesNotRequireStock && totalStock < 999999000 && hasPlanningReturnRisk(returnContext);
  const returnNotice = planningReturnWarning({ ...returnContext, returnDependencyRisk })
    ?? planningReturnNote({ ...returnContext, returnDependencyRisk });
  const capacityStatus = doesNotRequireStock
    ? 'green'
    : options?.statusOverride ?? computeIndicatorStatus(metrics, {
      isConfirmed: options?.isConfirmed,
      useServerConfirmedShortage: hasExactFirmTrancheAvailability,
    });
  const status = capacityStatus === 'green' && returnDependencyRisk ? 'yellow' : capacityStatus;
  const availabilityMode: AvailabilitySnapshot['availabilityMode'] = doesNotRequireStock
    ? 'not_required'
    : totalStock >= 999999000 || availableQty >= 999999000
      ? 'unlimited'
      : 'tracked';
  const tooltip = doesNotRequireStock
    ? 'No physical inventory is required for this item. It does not constrain event availability.'
    : buildSnapshotTooltip(metrics, details, {
      isConfirmed: options?.isConfirmed,
      useServerConfirmedShortage: hasExactFirmTrancheAvailability,
    });

  return {
    ...metrics,
    details,
    availabilityMode,
    capacityBasis: hasExactFirmTrancheAvailability && options?.isConfirmed ? 'event_allocation' : 'shared_stock',
    status,
    tooltip: returnNotice ? `${tooltip}\n${returnNotice}` : tooltip,
    planningReturns,
    returnDependencyRisk,
    freeBeforeCurrentQty,
    spareAfterCurrentQty: optionalQuantity(quantityMetadata?.spare_after_current_qty),
    quantityBasis: typeof quantityMetadata?.quantity_basis === 'string' ? quantityMetadata.quantity_basis : undefined,
    quantityWitnesses: Object.keys(quantityWitnesses).length ? quantityWitnesses : undefined,
  };
};

export const toInventoryAvailabilityFromQuoteOverlapSnapshot = (
  record: AvailabilityRecordInput | null | undefined,
  options?: { fallbackCurrentLineQty?: number; isConfirmed?: boolean },
): InventoryAvailability | null => {
  if (!record) return null;
  const snapshot = buildAvailabilitySnapshotFromRecord(record, options);
  return { snapshot, overlaps: snapshot.details };
};

export const toInventoryAvailabilityFromErpProductAvailability = (
  payload: any,
): ProductAvailability => ({
  status: payload?.status ? String(payload.status) : '',
  bestItemId: payload?.best_item_id ? String(payload.best_item_id) : null,
  fulfillQty: toNumber(payload?.fulfill_qty),
  directQty: toNumber(payload?.direct_qty),
  transferQty: toNumber(payload?.transfer_qty),
  usedMix: Boolean(payload?.used_mix),
  sameItemRequired: payload?.same_item_required == null ? null : Boolean(payload.same_item_required),
});

// Re-export availability window helper
export { computeAvailabilityWindow } from './availabilityWindow';
export type { AvailabilityWindowInput, AvailabilityWindowResult } from './availabilityWindow';

// Re-export anticipated dates helpers
export {
  fetchAnticipatedDates,
  extractDatePart,
  extractTimePart,
  computeDateDefaultsForRow,
} from './anticipatedDates';
export type { AnticipatedDates } from './anticipatedDates';

// Re-export anticipated dates hook
export { useAnticipatedDates, resolveDisplayDate } from './useAnticipatedDates';
export type { EventAnticipatedDates } from './useAnticipatedDates';
